import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/films';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <nav className="flex items-center justify-between px-4 md:px-6 h-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2">
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-800 hover:text-primary-500 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>

          <Link to="/" className="flex items-center gap-2 no-underline group">
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-movie-2-line text-2xl text-primary-500"></i>
            </div>
            <span className="font-heading text-xl font-bold text-foreground-950 whitespace-nowrap">
              木瓜影视
            </span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-1">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                (cat.slug === 'all' && location.pathname === '/') ||
                (cat.slug !== 'all' && location.pathname === `/category/${cat.slug}`)
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {searchOpen && (
            <div className="absolute top-0 left-0 right-0 h-16 bg-background-50 flex items-center px-4 md:px-6 z-10 animate-in fade-in">
              <div className="flex items-center bg-background-100 rounded-full px-4 py-2 w-full max-w-[600px] mx-auto border border-background-300">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                </div>
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="搜索影视、综艺、动漫..."
                  className="flex-1 bg-transparent text-foreground-800 text-sm outline-none px-3 placeholder:text-foreground-400"
                />
                <button
                  className="w-6 h-6 flex items-center justify-center text-foreground-500 hover:text-foreground-800 cursor-pointer"
                  onClick={() => setSearchOpen(false)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          )}

          <button
            className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
            onClick={() => setSearchOpen(true)}
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <Link
            to="/about"
            className="hidden md:flex items-center gap-1 text-foreground-600 hover:text-primary-500 transition-colors text-sm whitespace-nowrap cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-information-line"></i>
            </div>
            关于我们
          </Link>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 py-3 px-4 animate-in slide-in-from-top">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  (cat.slug === 'all' && location.pathname === '/') ||
                  (cat.slug !== 'all' && location.pathname === `/category/${cat.slug}`)
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
          <Link
            to="/about"
            className="block mt-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
          >
            关于我们
          </Link>
        </div>
      )}
    </header>
  );
}